import { useEffect, useId, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { GripVertical } from 'lucide-react';
import {
  Button,
  Card,
  FormField,
  Input,
  Select,
  Slider,
  StatusPill,
  Switch,
} from '@/components/ui';
import { getErrorMessage } from '@/api';
import type { MockupBlendMode } from '@/api/products';
import type {
  AdminMockupTemplate as ProductMockupTemplate,
  MockupLayerType,
  UpdateMockupTemplatePayload,
} from '@/api/admin';
import {
  useCreateMockupTemplate,
  useDeleteMockupTemplate,
  useRemoveMockupLayer,
  useUpdateMockupTemplate,
  useUploadMockupLayer,
} from '@/hooks/useAdminProducts';
import {
  normalizeLayerOrder,
  proxyStorageUrl,
} from '@/lib/product-transformer';

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const BLEND_MODES: MockupBlendMode[] = [
  'NORMAL',
  'MULTIPLY',
  'SCREEN',
  'OVERLAY',
  'SOFT_LIGHT',
  'HARD_LIGHT',
];

export const MOCKUP_TYPES = ['flat', 'person', 'lifestyle'];

const TOAST_OPTS = { position: 'bottom-right' as const };

interface LayerDefinition {
  type: MockupLayerType;
  label: string;
  description: string;
  required: boolean;
  hasBlendSettings: boolean;
}

/** Layers whose stacking order is editable via drag & drop */
const ORDERABLE_LAYERS: Record<string, LayerDefinition> = {
  base: {
    type: 'base',
    label: 'Base Image',
    description:
      'Schattierung des Produkts (Falten, Nähte). Liegt im Stapel über dem Design, damit die Falten das Motiv abdunkeln. Wie bei Shirtigo liegt das Design bei Tassen, Fußmatten, Kissen, bedruckten Kappen und Stickerei-Produkten auf dem Foto (Artikelkategorie).',
    required: true,
    hasBlendSettings: false,
  },
  heathered: {
    type: 'heathered',
    label: 'Heathered Overlay',
    description:
      'Melange texture, shown only on heathered colors. If empty, the creator uses a default heather pattern.',
    required: false,
    hasBlendSettings: true,
  },
  texture: {
    type: 'texture',
    label: 'Texture Overlay',
    description: 'Fabric folds, wrinkles, seams',
    required: false,
    hasBlendSettings: true,
  },
};

/** Layers with a fixed role outside the orderable stack */
const PINNED_LAYERS: LayerDefinition[] = [
  {
    type: 'shadow',
    label: 'Schatten',
    description:
      'Schattierung, Rahmen oder Kordeln. Liegt immer über Design und Texture, unter der Cutout-Maske.',
    required: false,
    hasBlendSettings: true,
  },
  {
    type: 'overlay',
    label: 'Overlay',
    description:
      'Liegt über dem gesamten Stapel und über der Cutout-Maske, wird nie beschnitten: Rahmen, Klippleisten, Aufhängungen, Kanten. Normaler Blend-Modus, volle Deckkraft.',
    required: false,
    hasBlendSettings: false,
  },
  {
    type: 'cutout',
    label: 'Cutout Mask',
    description:
      'Always clips the whole stack to the product shape. Luminance mask: white = visible, black = hidden.',
    required: false,
    hasBlendSettings: false,
  },
  {
    type: 'thumbnail',
    label: 'Thumbnail',
    description: 'Preview image for product lists (not part of the stack)',
    required: false,
    hasBlendSettings: false,
  },
];

function getLayerUrl(
  template: ProductMockupTemplate,
  type: MockupLayerType
): string | null {
  switch (type) {
    case 'base':
      return template.imageUrl;
    case 'heathered':
      return template.heatheredOverlayUrl ?? null;
    case 'texture':
      return template.textureOverlayUrl ?? null;
    case 'shadow':
      return template.shadowOverlayUrl ?? null;
    case 'overlay':
      return template.topOverlayUrl ?? null;
    case 'cutout':
      return template.cutoutMaskUrl ?? null;
    case 'thumbnail':
      return template.thumbnailUrl ?? null;
  }
}

/** Blend mode + opacity of a layer with blend settings, with the schema defaults */
function getBlendSettings(
  template: ProductMockupTemplate,
  type: MockupLayerType
): { blendMode: MockupBlendMode; opacity: number } {
  switch (type) {
    case 'heathered':
      return {
        blendMode: template.heatheredBlendMode ?? 'OVERLAY',
        opacity: template.heatheredOpacity ?? 0.5,
      };
    case 'shadow':
      return {
        blendMode: template.shadowBlendMode ?? 'MULTIPLY',
        opacity: template.shadowOpacity ?? 0.3,
      };
    default:
      return {
        blendMode: template.textureBlendMode ?? 'MULTIPLY',
        opacity: template.textureOpacity ?? 0.4,
      };
  }
}

function buildBlendPayload(
  type: MockupLayerType,
  blendMode: MockupBlendMode,
  opacity: number
): UpdateMockupTemplatePayload {
  switch (type) {
    case 'heathered':
      return { heatheredBlendMode: blendMode, heatheredOpacity: opacity };
    case 'shadow':
      return { shadowBlendMode: blendMode, shadowOpacity: opacity };
    default:
      return { textureBlendMode: blendMode, textureOpacity: opacity };
  }
}

// ═══════════════════════════════════════════════════════════════════
// LAYER ROW
// ═══════════════════════════════════════════════════════════════════

interface LayerRowProps {
  template: ProductMockupTemplate;
  layer: LayerDefinition;
  productId: string;
}

function LayerRow({ template, layer, productId }: LayerRowProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadLayer = useUploadMockupLayer(productId);
  const removeLayer = useRemoveMockupLayer(productId);
  const updateTemplate = useUpdateMockupTemplate(productId);

  const url = getLayerUrl(template, layer.type);
  const resolvedUrl = proxyStorageUrl(url);

  const blendSettings = getBlendSettings(template, layer.type);
  const blendMode = blendSettings.blendMode;
  const opacity = blendSettings.opacity;
  const missingCutoutWarning =
    layer.type === 'cutout' && !url
      ? 'Keine Cutout-Maske: Produktfarbe und Silhouette werden im Creator und im Rendering nicht dargestellt.'
      : null;
  const renderingAssetHint =
    layer.type === 'base' && template.renderingImageUrl
      ? 'Hochauflösend für Renderer und Canvas hinterlegt (2000 px)'
      : null;

  // Local value while dragging the slider; committed on release
  const [opacityDraft, setOpacityDraft] = useState(opacity);
  useEffect(() => setOpacityDraft(opacity), [opacity]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      await uploadLayer.mutateAsync({
        templateId: template.id,
        layerType: layer.type,
        file,
      });
      toast.success(`${layer.label} uploaded`, TOAST_OPTS);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  const handleRemove = async () => {
    try {
      await removeLayer.mutateAsync({
        templateId: template.id,
        layerType: layer.type,
      });
      toast.success(`${layer.label} removed`, TOAST_OPTS);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  const handleBlendSettingsChange = async (
    newBlendMode: MockupBlendMode,
    newOpacity: number
  ) => {
    try {
      await updateTemplate.mutateAsync({
        templateId: template.id,
        payload: buildBlendPayload(layer.type, newBlendMode, newOpacity),
      });
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  return (
    <div className="rounded-card p-3 ring-1 ring-line">
      <div className="flex items-start gap-3">
        {/* Layer preview */}
        <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-control bg-paper-3 ring-1 ring-line">
          {resolvedUrl ? (
            <img
              src={resolvedUrl}
              alt={layer.label}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-mute">
              Empty
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-ink">{layer.label}</p>
            {layer.required && (
              <StatusPill variant="warning">required</StatusPill>
            )}
          </div>
          <p className="text-xs text-ink-2">{layer.description}</p>
          {renderingAssetHint && (
            <p className="mt-1 text-xs text-mute">{renderingAssetHint}</p>
          )}
          {missingCutoutWarning && (
            <p
              role="alert"
              className="mt-1 rounded-control bg-warning-50 px-2 py-1 text-xs text-warning-700"
            >
              {missingCutoutWarning}
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              variant="outline"
              size="sm"
              loading={uploadLayer.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              {url ? 'Replace' : 'Upload'}
            </Button>
            {url && !layer.required && (
              <Button
                variant="ghost"
                size="sm"
                loading={removeLayer.isPending}
                onClick={handleRemove}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      {layer.hasBlendSettings && url && (
        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-line pt-3">
          <FormField label="Blend mode">
            <Select
              value={blendMode}
              onChange={(e) =>
                handleBlendSettingsChange(
                  e.target.value as MockupBlendMode,
                  opacity
                )
              }
            >
              {BLEND_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </Select>
          </FormField>
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-ink">
              Opacity: {Math.round(opacityDraft * 100)}%
            </span>
            <Slider
              aria-label="Opacity"
              min={0}
              max={1}
              step={0.05}
              value={[opacityDraft]}
              onValueChange={(values) =>
                setOpacityDraft(values[0] ?? opacityDraft)
              }
              onValueCommit={(values) =>
                handleBlendSettingsChange(blendMode, values[0] ?? opacity)
              }
              className="mt-3"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TEMPLATE CARD
// ═══════════════════════════════════════════════════════════════════

interface MockupTemplateCardProps {
  template: ProductMockupTemplate;
  productId: string;
}

function MockupTemplateCard({ template, productId }: MockupTemplateCardProps) {
  const updateTemplate = useUpdateMockupTemplate(productId);
  const deleteTemplate = useDeleteMockupTemplate(productId);

  // Layer stack order, displayed top layer first (like a layers panel).
  // Persisted bottom → top on the template.
  const topFirst = [...normalizeLayerOrder(template.layerOrder)].reverse();
  const [draggedLayer, setDraggedLayer] = useState<string | null>(null);

  const handleDropOnLayer = async (target: string) => {
    const dragged = draggedLayer;
    setDraggedLayer(null);
    if (!dragged || dragged === target) return;
    const next = topFirst.filter((l) => l !== dragged);
    next.splice(next.indexOf(target), 0, dragged);
    try {
      await updateTemplate.mutateAsync({
        templateId: template.id,
        payload: { layerOrder: [...next].reverse() },
      });
      toast.success('Layer order updated', TOAST_OPTS);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  // Two-step delete: first click arms the button, second click deletes
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  useEffect(() => {
    if (!confirmingDelete) return;
    const timer = setTimeout(() => setConfirmingDelete(false), 4000);
    return () => clearTimeout(timer);
  }, [confirmingDelete]);

  // Name is edited locally and committed on blur/Enter
  const [nameDraft, setNameDraft] = useState(template.name);
  useEffect(() => setNameDraft(template.name), [template.name]);

  const handleSetDefault = async () => {
    try {
      await updateTemplate.mutateAsync({
        templateId: template.id,
        payload: { isDefault: true },
      });
      toast.success('Set as default template', TOAST_OPTS);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  const handleSettingsChange = async (
    payload: Parameters<typeof updateTemplate.mutateAsync>[0]['payload']
  ) => {
    try {
      await updateTemplate.mutateAsync({ templateId: template.id, payload });
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  const commitName = () => {
    const name = nameDraft.trim();
    if (!name || name === template.name) {
      setNameDraft(template.name);
      return;
    }
    handleSettingsChange({ name });
  };

  const handleDelete = async () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    try {
      await deleteTemplate.mutateAsync(template.id);
      toast.success('Mockup template deleted', TOAST_OPTS);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  return (
    <Card padding="sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="dc-h3 truncate text-sm text-ink">{template.name}</h4>
            {template.isDefault && (
              <StatusPill variant="neutral">default</StatusPill>
            )}
          </div>
          <p className="text-xs text-ink-2">
            {template.mockupType} · {template.mockupWidthPx ?? '?'} ×{' '}
            {template.mockupHeightPx ?? '?'} px
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {!template.isDefault && (
            <Button
              variant="ghost"
              size="sm"
              loading={updateTemplate.isPending}
              onClick={handleSetDefault}
            >
              Set default
            </Button>
          )}
          <Button
            variant={confirmingDelete ? 'danger' : 'ghost'}
            size="sm"
            className={confirmingDelete ? '' : 'text-danger hover:bg-paper-3'}
            loading={deleteTemplate.isPending}
            onClick={handleDelete}
          >
            {confirmingDelete ? 'Confirm delete?' : 'Delete'}
          </Button>
        </div>
      </div>

      {/* Template settings */}
      <div className="mb-3 grid grid-cols-2 gap-3 rounded-control bg-paper-3 p-3">
        <FormField label="Name" className="col-span-2">
          <Input
            type="text"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          />
        </FormField>
        <div className="col-span-2 flex items-center justify-between gap-3">
          <div>
            <span className="text-sm font-medium text-ink">
              Editable in creator
            </span>
            <p className="text-xs text-mute">
              Whether users can design on this mockup
            </p>
          </div>
          <Switch
            aria-label="Editable in creator"
            checked={template.isCreatorEditable ?? true}
            onCheckedChange={(checked) =>
              handleSettingsChange({ isCreatorEditable: checked })
            }
          />
        </div>
      </div>

      <div className="mb-1">
        <h5 className="dc-eyebrow">Layer stack</h5>
        <p className="text-[11px] text-mute">
          Top layer first — drag to reorder. The creator renders in this order.
        </p>
      </div>
      <div className="space-y-2">
        {topFirst.map((layerKey) => {
          const layerDef =
            layerKey === 'design' ? null : ORDERABLE_LAYERS[layerKey];
          if (layerKey !== 'design' && !layerDef) return null;
          return (
            <div
              key={layerKey}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleDropOnLayer(layerKey);
              }}
              className={`flex items-stretch gap-1 ${
                draggedLayer === layerKey ? 'opacity-40' : ''
              }`}
            >
              <div
                draggable
                onDragStart={(e) => {
                  setDraggedLayer(layerKey);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnd={() => setDraggedLayer(null)}
                title="Drag to reorder"
                className="flex cursor-grab items-center px-0.5 text-mute transition-colors hover:text-ink-2 active:cursor-grabbing"
              >
                <GripVertical className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                {layerKey === 'design' ? (
                  <div className="rounded-card border border-dashed border-primary bg-primary-soft p-3">
                    <p className="text-sm font-medium text-ink">Design</p>
                    <p className="text-xs text-ink-2">
                      The customer&apos;s design, clipped to the design area
                    </p>
                  </div>
                ) : (
                  layerDef && (
                    <LayerRow
                      template={template}
                      layer={layerDef}
                      productId={productId}
                    />
                  )
                )}
              </div>
            </div>
          );
        })}

        <div className="border-t border-line pt-2">
          {PINNED_LAYERS.map((layer) => (
            <div key={layer.type} className="mb-2 last:mb-0">
              <LayerRow
                template={template}
                layer={layer}
                productId={productId}
              />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL
// ═══════════════════════════════════════════════════════════════════

interface MockupLayersPanelProps {
  productId: string;
  positionSlug: string;
  templates: ProductMockupTemplate[];
  /** Template selected in the chip row above the editor */
  activeTemplateId?: string | null;
}

/**
 * Manages the layers of the currently selected mockup template
 * of one product position.
 */
export function MockupLayersPanel({
  productId,
  positionSlug,
  templates,
  activeTemplateId,
}: MockupLayersPanelProps) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('flat');
  const [newFile, setNewFile] = useState<File | null>(null);
  const createTemplate = useCreateMockupTemplate(productId);
  // The base-image file input is a native control, so the field wiring
  // needs an explicit id to link its label.
  const newFileInputId = useId();

  const handleCreate = async () => {
    if (!newName.trim() || !newFile) {
      toast.error('Name and base image are required', TOAST_OPTS);
      return;
    }
    try {
      await createTemplate.mutateAsync({
        name: newName.trim(),
        positionKey: positionSlug,
        mockupType: newType,
        file: newFile,
      });
      toast.success('Mockup template created', TOAST_OPTS);
      setShowCreateForm(false);
      setNewName('');
      setNewType('flat');
      setNewFile(null);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  const activeTemplate =
    templates.find((t) => t.id === activeTemplateId) ??
    templates.find((t) => t.isDefault) ??
    templates[0];

  return (
    <div className="space-y-4">
      <p className="text-xs text-mute">
        Changes in this tab are saved immediately.
        {templates.length > 1 && ' Switch templates via the sidebar.'}
      </p>

      {templates.length === 0 && (
        <p className="rounded-card border border-dashed border-line p-4 text-center text-sm text-ink-2">
          No mockup templates for this position yet.
        </p>
      )}

      {activeTemplate && (
        <MockupTemplateCard
          key={activeTemplate.id}
          template={activeTemplate}
          productId={productId}
        />
      )}

      {showCreateForm ? (
        <div className="space-y-3 rounded-card p-4 ring-1 ring-inset ring-primary">
          <h4 className="dc-h3 text-sm text-ink">New mockup template</h4>
          <FormField label="Name">
            <Input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Front flat"
            />
          </FormField>
          <FormField label="Type">
            <Select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
            >
              {MOCKUP_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Base image" htmlFor={newFileInputId}>
            <input
              id={newFileInputId}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setNewFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-ink-2 file:mr-3 file:rounded-control file:border-0 file:bg-primary-soft file:px-3 file:py-1.5 file:font-display file:text-sm file:font-semibold file:text-primary hover:file:brightness-[0.95]"
            />
          </FormField>
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={createTemplate.isPending}
              onClick={handleCreate}
            >
              Create
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCreateForm(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setShowCreateForm(true)}
        >
          + Add mockup template
        </Button>
      )}
    </div>
  );
}
